<template>
  <div class="shifts-overview">
    <div>
      <form v-on:submit.prevent="submitSearch">
        <div class="d-flex justify-content-start">
          <div>
            <div class="label">Date</div>
            <board-item-date-picker v-model="searchDateRange" :customClass="'boardOverview'" :columns="2" :is-range="true" :mode="'date'" />
          </div>
        </div>
      </form>
    </div>

    <hr />

    <div class="shift-handovers-container">
      <div v-for="(shiftOverview, index) in shiftsOverview" :key="shiftOverview + index">
        <div class="shift-handovers">
          <div class="sh-header" @click="shiftOverview.isCollapsed = !shiftOverview.isCollapsed">
            <div class="shift-handover">
              <div class="ml-2 sh-toggle">
                <i
                  class="fas"
                  :class="{ 'fa-chevron-down': !shiftOverview.isCollapsed, 'fa-chevron-right': shiftOverview.isCollapsed }"
                ></i>
              </div>

              <div class="ml-1 sh-prop-m">
                {{ shiftOverview.handoverDateTime | formatDateWithoutHours }}
              </div>

              <div class="sh-prop-l ml-3">{{ shiftOverview.shift != null ? shiftOverview.shift.shiftType.name : 'Initial shift' }}</div>

              <div class="sh-prop-xl align-items-center">
                <i class="far fa-user mr-2"></i>
                {{ shiftOverview.shiftSupervisor }}
              </div>

              <div class="sh-prop-l d-flex justify-content-start">
                <div style="width: 25px">
                  {{ shiftOverview.relevantItemsCount }}
                </div>
                <div class="ml-2 badge badge-pill badge-info sh-prop-s">Relevant</div>
              </div>

              <div class="sh-prop-l d-flex justify-content-start">
                <div style="width: 25px">
                  {{ getItemsCount(shiftOverview.boardItemsSummary, itemsStatus.Planned).count }}
                </div>
                <div class="ml-2 badge badge-pill badge-primary sh-prop-s">
                  {{ getItemsCount(shiftOverview.boardItemsSummary, itemsStatus.Planned).status }}
                </div>
              </div>

              <div class="sh-prop-l d-flex justify-content-start">
                <div style="width: 25px">
                  {{ getItemsCount(shiftOverview.boardItemsSummary, itemsStatus.InProgress).count }}
                </div>
                <span class="ml-2 badge badge-pill badge-warning sh-prop-s">
                  {{ getItemsCount(shiftOverview.boardItemsSummary, itemsStatus.InProgress).status }}
                </span>
              </div>

              <div class="sh-prop-l d-flex justify-content-start">
                <div style="width: 25px">
                  {{ getItemsCount(shiftOverview.boardItemsSummary, itemsStatus.Done).count }}
                </div>
                <div class="pt-1 ml-2 badge badge-pill badge-success sh-prop-s">
                  {{ getItemsCount(shiftOverview.boardItemsSummary, itemsStatus.Done).status }}
                </div>
              </div>

              <div class="sh-prop-l d-flex justify-content-start">
                <div style="width: 25px">
                  {{ shiftOverview.sharedItemsCount }}
                </div>
                <div class="ml-2 badge badge-pill badge-secondary sh-prop-s">Shared</div>
              </div>
            </div>
          </div>

          <transition name="shift-details">
            <div v-if="!shiftOverview.isCollapsed">
              <div class="shift-handover-details">
                <hr class="mt-1 mb-2" />
                <ShiftOverview :shiftOverview="shiftOverview" class="ml-2 mt-2" />
              </div>
            </div>
          </transition>
        </div>
      </div>

      <div v-if="!shiftsOverview || !shiftsOverview.length">
        <div class="label justify-content-center d-flex">No Shifts found</div>
      </div>

      <div v-if="loading" class="d-flex align-items-center justify-content-center p-5">
        <loading-spinner></loading-spinner>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import Component from 'vue-class-component';
  import { OverviewModule, OverviewStore } from '@/store/overview';
  import DateRangeDto from '@/types/dateRangeDto';
  import BoardItemDatePicker from '@/components/BoardItem/BoardItemDatePicker.vue';
  import { Watch } from 'vue-property-decorator';
  import { BoardItemStatusDto, BoardItemsSummary, ShiftHandoverOverviewDto } from '@/apiclient/apiclient_generated';
  import ShiftOverview from './ShiftOverview.vue';
  import RequestParams from '@/types/requestParams/requestParams';
  import LoadingSpinner from '@/components/Misc/LoadingSpinner.vue';
  import { DateTime } from "luxon";

  @Component({
    components: {
      BoardItemDatePicker,
      ShiftOverview,
      LoadingSpinner,
    },
  })
  export default class ShiftsOverview extends Vue {
    private overviewStore: OverviewModule = OverviewStore;
    private searchDateRange: DateRangeDto = new DateRangeDto(DateTime.utc().plus({ days: -14 }).toString());

    private loading = false;
    private itemsStatus = BoardItemStatusDto;

    async created() {
      await this.submitSearch();
    }

    @Watch('searchDateRange')
    async changeFromDate() {
      await this.submitSearch();
    }

    async submitSearch() {
      this.loading = true;
      await this.overviewStore.loadShiftsHandoverOverview(this.requestParams);

      this.loading = false;
    }

    get shiftsOverview() {
      const data: ShiftHandoverOverviewDto[] = this.overviewStore.shiftsOverview;

      data.forEach((_) => {
        Vue.set(_, 'isCollapsed', true);
      });

      return data;
    }

    private getItemsCount(itemsSummary: BoardItemsSummary[], status: BoardItemStatusDto) {
      const data = itemsSummary.find((_) => _.status == status);

      return data || { count: 0, status };
    }

    get requestParams(): RequestParams {
      return {
        entityId: this.$route.params.id,
        orderByDesc: true,
        dateRangeFrom: this.searchDateRange.start,
        dateRangeTo: this.searchDateRange.end,
      } as RequestParams;
    }
  }
</script>

<style scoped lang="scss">
  .shift-handovers-container {
    height: 70vh;
    padding: 4px;
    overflow-y: auto;
  }

  .shift-handovers-container::-webkit-scrollbar {
    width: 8px;
  }

  .shift-handovers-container::-webkit-scrollbar-track {
    background: #ddd;
    border-radius: 5px;
  }

  .shift-handovers-container::-webkit-scrollbar-thumb {
    background: #999;
    border: 2px solid #ddd;
    border-radius: 5px;
  }

  .shift-handovers {
    background-color: white;
    box-shadow: 1px 1px 3px rgb(0 0 0 / 40%);
    border-radius: 3px;
    margin-bottom: 0.5em;

    .sh-header {
      outline: none;
      :hover {
        transition: background-color ease 0.4s, margin-top ease 0.4s;
        background-color: #e0edf1;
        cursor: pointer;
      }
    }
    .shift-handover {
      padding: 10px 5px;

      display: flex;
      justify-content: space-between;
      align-items: center;

      :hover {
        transition: background-color ease 0.4s, margin-top ease 0.4s;
        background-color: #e0edf1;
      }

      .sh-toggle {
        min-width: 25px;
      }

      .sh-report {
        justify-content: end;
      }

      .sh-prop-s {
        width: 75px;
        padding-top: 5px;
      }

      .sh-prop-m {
        width: 140px;
      }

      .sh-prop-l {
        width: 150px;
        flex-shrink: 0;
      }
      .sh-prop-xl {
        width: 250px;
        flex-shrink: 0;
      }
    }

    .shift-handover-details {
      padding: 4px;
    }
  }

  .shift-details-enter-active,
  .shift-details-leave-active {
    transition: opacity 0.5s;
  }
  .shift-details-enter,
  .shift-details-leave-to {
    opacity: 0;
  }
</style>
